import React, { useState, useEffect } from "react";
import { Card, CardBody, Table, Button, Spinner } from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { fetchWorkflowSteps, createStep, updateStep, deleteStep, fetchMappingByRecruiterId, fetchMappedSteps } from "./Axios";
import StepForm from "./StepForm";
import CandidateStepForm from "./CandidateStepForm";
import StepView from "./StepView";


const WorkflowEditor = ({ workflow, onClose }) => {
  const [steps, setSteps] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [viewingStep, setViewingStep] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workflow) loadSteps();
  }, [workflow]);


  const loadSteps = async () => {
    setLoading(true);
    try {
      const data = await fetchWorkflowSteps(workflow.id);
      setSteps([...data].sort((a, b) => a.position - b.position));
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    if (window.confirm("Al modificar los steps, se reiniciará la configuración del mapeo. ¿Deseas continuar?")) {
      const reorderedSteps = [...steps];
      const [movedStep] = reorderedSteps.splice(result.source.index, 1);
      reorderedSteps.splice(result.destination.index, 0, movedStep);

      const updatedSteps = reorderedSteps.map((step, index) => ({ ...step, position: index + 1 }));

      setSteps(updatedSteps);
      saveStepOrder(updatedSteps).then(resetMappedSteps);
    }
  };

  const saveStepOrder = (updatedSteps) =>
    Promise.all(updatedSteps.map(step => updateStep(step.id, { position: step.position })));

  const handleAddStep = async (stepData, stepId) => {
    try {
      if (stepId) {
        await updateStep(stepId, stepData);
      } else {
        const newPosition = steps.length ? Math.max(...steps.map(s => s.position)) + 1 : 1;
        await createStep(workflow.id, { ...stepData, position: newPosition });
      }
      loadSteps();
    } finally {
      setEditingStep(null);
      setIsFormOpen(false);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (workflow.locked) return;

    if (window.confirm("⚠ Al eliminar este step, se reiniciará la configuración del mapeo. ¿Deseas continuar?")) {
      try {
        await deleteStep(stepId);

        setSteps(prevSteps => {
          const updatedSteps = prevSteps
            .filter(step => step.id !== stepId)
            .map((step, index) => ({ ...step, position: index + 1 }));

          saveStepOrder(updatedSteps).then(loadSteps);
          return updatedSteps;
        });

        resetMappedSteps();
      } catch {}
    }
  };

  const handleEditStep = (step) => {
    if (!step) return;

    const stepWithCustomFields = { ...step, custom_fields: step.custom_fields ? [...step.custom_fields] : [] };

    step.locked ? setViewingStep(step) : setEditingStep(stepWithCustomFields);
    setIsFormOpen(!step.locked);
  };

  const resetMappedSteps = async () => {
    const mapping = await fetchMappingByRecruiterId(workflow.id);
    if (mapping) {
      const mappedSteps = await fetchMappedSteps(mapping.id);
      await Promise.all(mappedSteps.map(step => deleteMappedStep(mapping.id, step.id)));
    }
  };


  return (
    <Card className="mt-4">
      <CardBody>
        <h1 className="fs-4">
          <span className="small d-block">Editar Workflow:{" "}</span>
          <span className="fw-bold">{workflow.name}</span>
        </h1>

        {loading ? (
          <Spinner color="primary" className="mt-3" />
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="steps-list">
              {(provided) => (
                <Table innerRef={provided.innerRef} {...provided.droppableProps} className="mt-3 table-hover">
                  <thead>
                  <tr>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Requerido</th>
                    {!workflow.is_candidate_workflow && workflow.locked &&  (
                      <th className="text-end">Acciones</th>
                    )}
                  </tr>
                  </thead>
                  <tbody>
                  {steps.length > 0 ? (
                    steps.map((step, index) => (
                      <Draggable key={step.id} draggableId={step.id.toString()} index={index} isDragDisabled={workflow.locked}>
                        {(provided) => (
                          <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                            <td>{step.position}</td>
                            <td>{step.name}</td>
                            <td>{step.kind || "Ninguno"}</td>
                            <td>{step.required ? "si" : "no"}</td>
                            <td className="text-end">
                              <Button color="info" size="sm" className="mr-2" onClick={() => handleEditStep(step)}>
                                {step.locked ? "Ver" : "Editar"}
                              </Button>
                              {!workflow.locked && (
                                <Button color="danger" size="sm" onClick={() => handleDeleteStep(step.id)}>
                                  Eliminar
                                </Button>
                              )}
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">No hay steps disponibles.</td>
                    </tr>
                  )}
                  {provided.placeholder}
                  </tbody>
                </Table>
              )}
            </Droppable>
          </DragDropContext>
        )}
        <div className="mt-3 d-flex justify-content-between">
          {!workflow.locked && (
            <Button
              color="primary"
              onClick={() => {
                setEditingStep({
                  name: "",
                  description: "",
                  kind: "",
                  label: "",
                  required: false,
                  custom_fields: []
                });
                setIsFormOpen(true);
              }}
            >
              Agregar Step
            </Button>
          )}
          <Button color="secondary" onClick={onClose}>Cerrar</Button>
        </div>

        {workflow.is_candidate_workflow ? (
          <CandidateStepForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} onSave={handleAddStep} step={editingStep} />
        ) : (
          <>
          <StepForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} onSave={handleAddStep} step={editingStep} />
          </>
        )}
        <StepView isOpen={!!viewingStep} onClose={() => setViewingStep(null)} step={viewingStep} />
      </CardBody>
    </Card>
  );
};


export default WorkflowEditor;
